<template>
	<div 
		class="pik-widget multi-posttest w-full h-full text-sm leading-4"
	>
		<template v-if="isMissingJobnums">
			<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
				<p>Missing Jobnumbers</p>
			</div>
		</template>
		<template v-else-if="isMissingToken">
			<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
				<p>Please <a 
					class="underline" 
					:href="loginUrl"
					style="text-underline-offset: 4px;"
				>login</a> to complete posttest for credit.</p>
			</div>
		</template>
		<template v-else-if="isError">
			<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
				<p v-html="errorMessage"></p>
			</div>
		</template>
		<template v-else-if="isCheckingStatus">
			<div class="w-full min-height h-full flex flex-row justify-center items-center">
				<LoadingSpinner />
			</div>
		</template>
		<template v-else-if="phase === 'interstitial'">
			<div class="w-full h-full flex flex-col justify-center items-center">
				<div class="w-full max-w-md text-center p-x2 flex flex-col gap-x2">
					<div v-if="interstitialContent" class="flex flex-col gap-x1" v-html="interstitialContent"></div>
					<div class="flex flex-col gap-x1">
						<button 
							v-if="hasNextJobnum"
							class="w-full p-x1 laptop:p-x1.5 border-1 border-solid border-black rounded-default bg-black text-white text-sm"
							@click="handleContinueToNext"
							v-html="interstitialContinueButtonLabel"
						></button>
						<button 
							@click="handleInterstitialCancel"
							v-html="interstitialCancelButtonLabel"
						></button>
					</div>
				</div>
			</div>
		</template>
		<template v-else-if="phase === 'end' || isExhausted">
			<div class="w-full h-full flex flex-col justify-center items-center">
				<div class="w-full max-w-md text-center p-x2 flex flex-col gap-x2">
					<div v-if="endContent" class="flex flex-col gap-x1" v-html="endContent"></div>
					<div class="flex flex-col gap-x1">
						<button 
							class="w-full p-x1 laptop:p-x1.5 border-1 border-solid border-black rounded-default bg-black text-white text-sm"
							@click="handleEndContinue"
							v-html="endContinueButtonLabel"
						></button>
						<button 
							@click="handleEndCancel"
							v-html="endCancelButtonLabel"
						></button>
					</div>
				</div>
			</div>
		</template>
		<template v-else-if="phase === 'success'">
			<div class="w-full h-full flex flex-col justify-center items-center">
				<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
					<p>All posttests completed successfully!</p>
				</div>
			</div>
		</template>
		<template v-else-if="currentJobnum">
			<pik-posttest
				:key="currentJobnum"
				:jobnum="currentJobnum"
				:token="token"
				:login-redirect-url="loginRedirectUrl"
				:source="source"
				@on-loaded="onPosttestLoaded"
				@is-completed="(event) => handleJobnumCompleted(event)"
				@submit-posttest="(event) => emit('submit-posttest', event)"
				@continue-posttest="(event) => emit('continue-posttest', event)"
				@credit-claimed="(event) => emit('credit-claimed', event)"
			></pik-posttest>
		</template>
	</div>
</template>

<script setup>
import { defineEmits, defineProps, ref, computed, onMounted, watch } from 'vue'
import { LoadingSpinner } from '@/components/'

const emit = defineEmits(['is-completed', 'submit-posttest', 'continue-posttest', 'credit-claimed'])

// Props
const props = defineProps({
	jobnums: {
		type: [Array, String],
		required: true,
		default: () => []
	},
	token: {
		type: String,
		default: null
	},
	loginRedirectUrl: {
		type: String,
		default: 'https://suiteweb.atpointofcare.com/#user/login'
	},
	source: {
		type: String,
		default: 'suite-web'
	},
	interstitialContent: {
		type: String,
		default: 'You have finished this section. Would you like to continue to the next section?'
	},
	interstitialContinueButtonLabel: {
		type: String,
		default: 'Continue'
	},
	interstitialCancelButtonLabel: {
		type: String,
		default: 'Cancel'
	},
	endContent: {
		type: String,
		default: 'You have finished all sections. Would you like to continue to Claim Credit?'
	},
	endContinueButtonLabel: {
		type: String,
		default: 'Claim Credit'
	},
	endCancelButtonLabel: {
		type: String,
		default: 'Cancel'
	}
})

const claimCreditCheckEndpoint = computed(() => process.env.VUE_APP_CLAIMCREDITCHECK_API_URL)

// State
// phase: null = show current test, 'interstitial' = between sections, 'end' = all done / claim credit, 'success' = final message
const currentJobnumIndex = ref(0)
const phase = ref(null)
const isCheckingStatus = ref(false)
const isError = ref(false)
const errorMessage = ref(null)
// True until PostTest has emitted on-loaded (test loaded for this jobnum). Prevents interstitial when PostTest reports complete on load.
const isFirstLoad = ref(true)
// Prevents running initial status check twice when jobnums arrive after mount (e.g. CE attributes).
const initialCheckDone = ref(false)

// Computed
const loginUrl = computed(() => {
	const base = props.loginRedirectUrl
	const params = `redirect=true&redirect_url=${ encodeURIComponent(window.location) }`
	if (base.includes('#')) {
		const split = base.split('#')
		return `${ split[0] }?${ params }#${ split[1] }`
	}
	else {
		return `${ base }?${ params }`
	}
})

const isMissingToken = computed(() => {
  return !props.token || (props.token && (props.token === 'undefined' || props.token === 'null'))
})

// Normalize jobnums so we always work with an array (prop can arrive as string from custom element attributes)
const jobnumsList = computed(() => {
	const raw = props.jobnums
	if (!raw) return []
	if (Array.isArray(raw)) return raw
	const str = String(raw).trim()
	if (!str) return []
	return str.split(',').map(j => j.trim()).filter(j => j.length > 0)
})

const isMissingJobnums = computed(() => {
  return jobnumsList.value.length === 0
})

const currentJobnum = computed(() => {
	if (currentJobnumIndex.value >= 0 && currentJobnumIndex.value < jobnumsList.value.length) {
		return jobnumsList.value[currentJobnumIndex.value]
	}
	return null
})

// Safety net: the index has advanced past the last posttest (all complete). The
// navigation logic sets phase='end' in this case, but this guarantees the widget
// shows the end / Claim Credit screen — never a blank body — even if some future
// path leaves phase unset.
const isExhausted = computed(() => {
	return jobnumsList.value.length > 0 && currentJobnumIndex.value >= jobnumsList.value.length
})

const hasNextJobnum = computed(() => {
	return currentJobnumIndex.value < jobnumsList.value.length - 1
})

// Check if a single jobnum is complete (same logic as PostTest)
const checkJobnumComplete = async (jobnum) => {
	const url = `${ claimCreditCheckEndpoint.value }/${ jobnum }?token=${ props.token }`
	const req = await fetch(url)
	const res = await req.json()
	if (res.error) throw new Error(res.error)
	return !!(res.posttest?.complete && res.eval?.complete)
}

// Find first incomplete jobnum index starting from startFrom (inclusive). Returns index or jobnumsList.length if all complete.
const findFirstIncompleteIndex = async (startFrom = 0) => {
	const list = jobnumsList.value
	for (let i = startFrom; i < list.length; i++) {
		const complete = await checkJobnumComplete(list[i])
		if (!complete) return i
	}
	return list.length
}

const goToNextIncomplete = async (jobnum) => {
	isCheckingStatus.value = true
	isError.value = false
	try {
		const list = jobnumsList.value
		const fromIndex = list.indexOf(jobnum)
		const nextIndex = await findFirstIncompleteIndex(fromIndex + 1)
		currentJobnumIndex.value = nextIndex
		// Past the last posttest means every posttest is done (we always start at the
		// first incomplete and only move forward), so show the end / Claim Credit
		// screen — regardless of which jobnum finished last. Posttests can be
		// completed out of order, so keying this on "was the last index" left the
		// widget in an unhandled state (blank body) when an earlier-indexed job was
		// the final one completed.
		if (nextIndex >= list.length) {
			phase.value = 'end'
		}
	} catch (err) {
		isError.value = true
		errorMessage.value = err?.message || err
	} finally {
		isCheckingStatus.value = false
	}
}

// Run once when we have jobnums and token. Used by onMounted and watcher (when jobnums arrive after mount).
const runInitialStatusCheck = async () => {
	const list = jobnumsList.value
	if (list.length === 0) return
	isCheckingStatus.value = true
	isError.value = false
	try {
		const firstIncomplete = await findFirstIncompleteIndex(0)
		currentJobnumIndex.value = firstIncomplete
		if (firstIncomplete >= list.length) {
			phase.value = 'end'
		}
	} catch (err) {
		isError.value = true
		errorMessage.value = err?.message || err
	} finally {
		isCheckingStatus.value = false
	}
}

const onPosttestLoaded = () => {
	isFirstLoad.value = false
}

const handleJobnumCompleted = async (event) => {
	const { jobnum } = event?.detail?.[0]
	if (isFirstLoad.value) {
		// Complete on load (we never got on-loaded) — skip interstitial, go to next incomplete
		await goToNextIncomplete(jobnum)
		return
	}
	const nextIndex = await findFirstIncompleteIndex(currentJobnumIndex.value + 1)
	// Past the last posttest → all posttests complete → end / Claim Credit screen.
	// Otherwise there is still an incomplete posttest ahead → show the interstitial.
	// (Completion can happen out of order, so this must not depend on which jobnum
	// finished last.)
	if (nextIndex >= jobnumsList.value.length) {
		phase.value = 'end'
	} else {
		phase.value = 'interstitial'
	}
}

const handleContinueToNext = async () => {
	phase.value = null
	isFirstLoad.value = true
	await goToNextIncomplete(currentJobnum.value)
}

const handleInterstitialCancel = () => {
	phase.value = null
	emit('on-interstitial-cancel')
}

const handleEndContinue = () => {
	phase.value = 'success'
	emit('on-end-continue', { status: 'complete', jobnums: jobnumsList.value, token: props.token })
}

const handleEndCancel = () => {
	phase.value = 'success'
	emit('on-end-cancel')
}

onMounted(async () => {
	if (isMissingToken.value) return
	if (jobnumsList.value.length === 0) return
	initialCheckDone.value = true
	await runInitialStatusCheck()
})

// When jobnums arrive after mount (e.g. custom element attributes not yet set), run the initial check once.
watch(
	() => jobnumsList.value.length,
	(len) => {
		if (len > 0 && !initialCheckDone.value && !isMissingToken.value) {
			initialCheckDone.value = true
			runInitialStatusCheck()
		}
	}
)
</script>

<style src="@/assets/css/tailwind.css"></style>
<style src="@/assets/css/_fontface.css"></style>
<style src="@/assets/css/_variables.css"></style>
<style src="@/assets/css/_global.css"></style>
<style src="@/assets/css/_animations.css"></style>
<style src="@/assets/css/main.css"></style>
